import { p as proxyCustomElement, H, d as createEvent, h } from './p-23afa4c1.js';
import { b as bem } from './p-a05fe53c.js';
import { i as isNodeEnvironment } from './p-2f624564.js';
import { r as resolveAppearance } from './p-d1ae9dd0.js';

const IMAGE_STATE_LOAD_SUCCESS = 'success';
const IMAGE_STATE_LOAD_ERROR = 'error';
const IMAGE_STATE_LOADING = 'loading';

const cosmosImageCss = ":host{display:block;height:100%}.cosmos-image{display:-ms-flexbox;display:flex;height:100%;isolation:isolate;min-height:inherit;overflow:hidden}.cosmos-image--radius-none{border-radius:var(--cosmos-image-border-radius, 0)}.cosmos-image--radius-small{border-radius:var(--cosmos-image-border-radius, 2px)}.cosmos-image--radius-medium{border-radius:var(--cosmos-image-border-radius, 6px)}.cosmos-image--radius-large{border-radius:var(--cosmos-image-border-radius, 8px)}.cosmos-image--radius-full{border-radius:var(--cosmos-image-border-radius, 9999px)}.cosmos-image--fallback.cosmos-image--appearance-light{background-color:rgba(0, 15, 30, 0.05)}.cosmos-image--fallback.cosmos-image--appearance-dark{background-color:rgba(255, 255, 255, 0.1)}.cosmos-image--image-state-loading,.cosmos-image--fit-contain{background-color:transparent}.cosmos-image__container{display:grid;grid-template-areas:\"image\";height:100%;width:100%}.cosmos-image--has-preview:not(.cosmos-image--has-image) .cosmos-image__container{display:-ms-flexbox;display:flex}.cosmos-image--fit-contain .cosmos-image__container{place-content:center}.cosmos-image__preview-container,.cosmos-image__image{grid-area:image;max-height:100%;min-height:0}.cosmos-image--fit-contain.cosmos-image--radius-none .cosmos-image__preview-container,.cosmos-image--fit-contain.cosmos-image--radius-none .cosmos-image__image{border-radius:var(--cosmos-image-border-radius, 0)}.cosmos-image--fit-contain.cosmos-image--radius-small .cosmos-image__preview-container,.cosmos-image--fit-contain.cosmos-image--radius-small .cosmos-image__image{border-radius:var(--cosmos-image-border-radius, 2px)}.cosmos-image--fit-contain.cosmos-image--radius-medium .cosmos-image__preview-container,.cosmos-image--fit-contain.cosmos-image--radius-medium .cosmos-image__image{border-radius:var(--cosmos-image-border-radius, 6px)}.cosmos-image--fit-contain.cosmos-image--radius-large .cosmos-image__preview-container,.cosmos-image--fit-contain.cosmos-image--radius-large .cosmos-image__image{border-radius:var(--cosmos-image-border-radius, 8px)}.cosmos-image--fit-contain.cosmos-image--radius-full .cosmos-image__preview-container,.cosmos-image--fit-contain.cosmos-image--radius-full .cosmos-image__image{border-radius:var(--cosmos-image-border-radius, 9999px)}.cosmos-image__preview-image,.cosmos-image__image{-o-object-fit:cover;object-fit:cover}.cosmos-image__preview-container{display:-ms-flexbox;display:flex;overflow:hidden;position:relative}.cosmos-image--fit-cover .cosmos-image__preview-container{width:100%}.cosmos-image__preview-image{-webkit-filter:blur(5px);filter:blur(5px);height:calc(100% + 5px * 2);inset:0;position:absolute;-webkit-transform:translate(-5px, -5px);transform:translate(-5px, -5px);width:calc(100% + 5px * 2)}.cosmos-image__preview-dummy{max-width:100%}.cosmos-image--has-preview:not(.cosmos-image--has-image) .cosmos-image__preview-dummy{width:100%}.cosmos-image__image{max-width:100%;-webkit-transition:opacity 0.2s cubic-bezier(0, 0.32, 0.32, 1);transition:opacity 0.2s cubic-bezier(0, 0.32, 0.32, 1);z-index:1}.cosmos-image--image-state-loading .cosmos-image__image{opacity:0}.cosmos-image--image-state-success .cosmos-image__image{opacity:1;width:100%}.cosmos-image--fit-cover .cosmos-image__image{height:100%}";
const CosmosImageStyle0 = cosmosImageCss;

const generateClassNames = bem('cosmos-image');
const CosmosImage = /*@__PURE__*/ proxyCustomElement(class CosmosImage extends H {
    handleAppearanceChange(appearance) {
        this.computedAppearance = appearance;
    }
    handleImageChange() {
        this.imageState = IMAGE_STATE_LOADING;
    }
    handleObserveResizeChange(newValue) {
        if (this.containerResizeObserver) {
            this.containerResizeObserver.unobserve(this.container);
            if (newValue) {
                this.containerResizeObserver.observe(this.container);
            }
        }
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.resize = createEvent(this, "resize", 5);
        this.handleImageLoad = () => {
            this.imageState = IMAGE_STATE_LOAD_SUCCESS;
        };
        this.handleImageError = () => {
            this.imageState = IMAGE_STATE_LOAD_ERROR;
        };
        this.handlePreviewError = () => {
            this.previewState = IMAGE_STATE_LOAD_ERROR;
        };
        this.shouldDisplayFallback = () => {
            return !(this.shouldDisplayImage() || this.shouldDisplayPreview());
        };
        this.shouldDisplayImage = () => {
            return !!this.image && this.imageState !== IMAGE_STATE_LOAD_ERROR;
        };
        this.shouldDisplayPreview = () => {
            return !!this.imagePreview && this.previewState !== IMAGE_STATE_LOAD_ERROR;
        };
        this.appearance = undefined;
        this.fit = 'cover';
        this.image = undefined;
        this.imageAlt = '';
        this.imagePreview = undefined;
        this.loading = undefined;
        this.observeResize = undefined;
        this.radius = 'none';
        this.sizes = undefined;
        this.srcset = undefined;
        this.computedAppearance = 'light';
        this.imageState = undefined;
        this.previewState = undefined;
        if (isNodeEnvironment()) {
            return;
        }
        this.containerResizeObserver = new ResizeObserver((entries) => this.handleContainerResize(entries));
    }
    componentWillLoad() {
        resolveAppearance({
            instance: this,
            modeToAppearanceMap: { dark: 'dark', light: 'light' },
            callback: this.handleAppearanceChange.bind(this),
        });
        if (this.image) {
            this.imageState = IMAGE_STATE_LOADING;
        }
    }
    componentDidLoad() {
        if (this.observeResize) {
            this.handleObserveResizeChange(this.observeResize);
        }
    }
    handleContainerResize(entries) {
        entries.forEach((entry) => {
            if (entry.target === this.container) {
                const { height, width } = entry.contentRect;
                this.resize.emit({
                    height,
                    width,
                });
            }
        });
    }
    render() {
        return (h("div", { key: 'f9ec3e2166b339d385672c81c31254d213c3c697', class: generateClassNames({
                'appearance': this.computedAppearance,
                'fallback': this.shouldDisplayFallback(),
                'fit': this.fit,
                'has-image': !!this.image,
                'has-preview': !!this.imagePreview,
                'image-state': this.imageState,
                'radius': this.radius,
            }), part: "base", ref: (element) => (this.container = element) }, !this.shouldDisplayFallback() && (h("div", { class: generateClassNames('container') }, this.shouldDisplayPreview() && (h("div", { class: generateClassNames('preview-container') }, h("img", { alt: "", class: generateClassNames('preview-image'), onError: this.handlePreviewError, src: this.imagePreview }), h("img", { alt: "", class: generateClassNames('preview-dummy'), src: this.imagePreview }))), this.shouldDisplayImage() && (h("img", { alt: this.imageAlt, class: generateClassNames('image'), loading: this.loading, onError: this.handleImageError, onLoad: this.handleImageLoad, part: "image", sizes: this.sizes, src: this.image, srcSet: this.srcset }))))));
    }
    get element() { return this; }
    static get watchers() { return {
        "appearance": ["handleAppearanceChange"],
        "image": ["handleImageChange"],
        "observeResize": ["handleObserveResizeChange"]
    }; }
    static get style() { return CosmosImageStyle0; }
}, [1, "cosmos-image", {
        "appearance": [513],
        "fit": [513],
        "image": [513],
        "imageAlt": [513, "image-alt"],
        "imagePreview": [513, "image-preview"],
        "loading": [513],
        "observeResize": [516, "observe-resize"],
        "radius": [513],
        "sizes": [513],
        "srcset": [513],
        "computedAppearance": [32],
        "imageState": [32],
        "previewState": [32]
    }, undefined, {
        "appearance": ["handleAppearanceChange"],
        "image": ["handleImageChange"],
        "observeResize": ["handleObserveResizeChange"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["cosmos-image"];
    components.forEach(tagName => { switch (tagName) {
        case "cosmos-image":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, CosmosImage);
            }
            break;
    } });
}

export { CosmosImage as C, defineCustomElement as d };
